import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import PublicLayout from "@/components/PublicLayout";
import { useSiteContent } from "@/context/SiteContentContext";
import { Button } from "@/components/ui/button";
import { Box, GraduationCap, Cpu, Check, ArrowRight, Plus, Minus, UploadCloud } from "lucide-react";

const ACCENTS = {
  teal: { chip: "bg-teal-600", ring: "ring-teal-500", text: "text-teal-600", soft: "bg-teal-50", check: "text-teal-600", grad: "from-teal-600 to-teal-500" },
  rose: { chip: "bg-rose-500", ring: "ring-rose-400", text: "text-rose-500", soft: "bg-rose-50", check: "text-rose-500", grad: "from-rose-500 to-rose-400" },
  slate: { chip: "bg-slate-800", ring: "ring-slate-500", text: "text-slate-200", soft: "bg-slate-100", check: "text-slate-200", grad: "from-slate-800 to-slate-700" },
};

export default function ServicesPage() {
  const { content, resolveImg } = useSiteContent();
  const s = content.servizi;
  const [openId, setOpenId] = useState("produzione");
  const toggle = (id) => setOpenId((cur) => (cur === id ? null : id));

  const AREAS = [
    {
      id: "produzione", icon: Box, accent: "teal",
      kicker: s.produzione_kicker, title: s.produzione_title, tagline: s.produzione_tagline,
      body: s.produzione_body, steps: s.produzione_steps,
      cta: { label: s.produzione_cta, to: "/login" },
    },
    {
      id: "formazione", icon: GraduationCap, accent: "rose",
      kicker: s.formazione_kicker, title: s.formazione_title, tagline: s.formazione_tagline,
      groups: [{ heading: "I nostri corsi", items: s.formazione_items }],
      cta: { label: s.formazione_cta, to: "/contatti" },
    },
    {
      id: "sviluppo", icon: Cpu, accent: "slate",
      kicker: s.sviluppo_kicker, title: s.sviluppo_title, tagline: s.sviluppo_tagline,
      body: s.sviluppo_body, groups: [{ heading: "Progetti e novità", items: s.sviluppo_items }],
      cta: { label: s.sviluppo_cta, to: "/contatti" },
    },
  ];

  return (
    <PublicLayout>
      <section className="pt-32 pb-16 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-sm font-bold uppercase tracking-widest text-rose-400">{s.hero_kicker}</p>
          <h1 className="font-heading text-4xl sm:text-5xl font-extrabold mt-2">{s.hero_title}</h1>
          <p className="mt-4 text-slate-300 max-w-2xl text-lg">{s.hero_subtitle}</p>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-5">
            {AREAS.map((a, i) => {
              const c = ACCENTS[a.accent];
              const active = openId === a.id;
              const Icon = a.icon;
              return (
                <motion.button
                  key={a.id}
                  data-testid={`service-card-${a.id}`}
                  onClick={() => toggle(a.id)}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.08 }}
                  className={`text-left rounded-2xl border p-6 bg-slate-900/70 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl ${active ? `ring-2 ${c.ring} border-transparent shadow-lg` : "border-white/10"}`}
                >
                  <div className="flex items-center justify-between">
                    <div className={`h-12 w-12 rounded-xl ${c.chip} flex items-center justify-center shadow`}>
                      <Icon className="h-6 w-6 text-white" />
                    </div>
                    <span className={`h-8 w-8 rounded-full ${c.soft} ${c.text} flex items-center justify-center transition-transform`}>
                      {active ? <Minus className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
                    </span>
                  </div>
                  <p className={`mt-5 text-xs font-bold uppercase tracking-wider ${c.text}`}>{a.kicker}</p>
                  <h3 className="font-heading text-xl font-bold text-white mt-1">{a.title}</h3>
                  <p className="text-sm text-slate-400 mt-2 leading-relaxed">{a.tagline}</p>
                </motion.button>
              );
            })}
          </div>

          <div className="mt-6">
            <AnimatePresence initial={false} mode="wait">
              {openId && (() => {
                const a = AREAS.find((x) => x.id === openId);
                const c = ACCENTS[a.accent];
                const Icon = a.icon;
                return (
                  <motion.div
                    key={a.id}
                    data-testid={`service-panel-${a.id}`}
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    <div className="rounded-2xl border border-white/10 bg-slate-900/60 p-8 grid md:grid-cols-3 gap-8">
                      <div className="md:col-span-1">
                        <div className={`rounded-2xl h-44 bg-gradient-to-br ${c.grad} flex items-center justify-center relative overflow-hidden shadow-lg`}>
                          <img src={resolveImg(content.general.logo)} alt="" aria-hidden className="absolute -right-8 -bottom-8 h-40 opacity-10 brightness-0 invert" />
                          <Icon className="h-16 w-16 text-white/95 relative" />
                        </div>
                        <Link to={a.cta.to}>
                          <Button data-testid={`service-cta-${a.id}`} className={`w-full mt-4 ${c.chip} hover:opacity-90 rounded-full font-semibold`}>
                            {a.cta.label} <ArrowRight className="h-4 w-4 ml-1.5" />
                          </Button>
                        </Link>
                      </div>
                      <div className="md:col-span-2">
                        {a.body && <p className="text-slate-300 leading-relaxed">{a.body}</p>}

                        {a.steps && (
                          <div className="mt-6 grid sm:grid-cols-3 gap-4">
                            {a.steps.map((st, idx) => (
                              <div key={st} className="rounded-xl bg-slate-900/70 border border-white/10 p-4">
                                <div className={`h-8 w-8 rounded-full ${c.chip} text-white flex items-center justify-center text-sm font-bold mb-3`}>{idx + 1}</div>
                                <p className="text-sm text-slate-200 leading-snug">{st}</p>
                              </div>
                            ))}
                          </div>
                        )}

                        {a.steps && (
                          <div className={`mt-6 flex items-center gap-3 rounded-xl ${c.soft} p-4`}>
                            <UploadCloud className={`h-6 w-6 ${c.text} shrink-0`} />
                            <p className="text-sm text-slate-200">Carica STL, XML, immagini e note direttamente dalla piattaforma, in totale sicurezza.</p>
                          </div>
                        )}

                        {a.groups && (
                          <div className="grid sm:grid-cols-2 gap-8">
                            {a.groups.map((g) => (
                              <div key={g.heading}>
                                <h4 className="font-heading font-bold text-white mb-4">{g.heading}</h4>
                                <ul className="space-y-2.5">
                                  {(g.items || []).map((it) => (
                                    <li key={it} className="flex items-start gap-2 text-slate-200">
                                      <Check className={`h-4 w-4 mt-0.5 shrink-0 ${c.check}`} />
                                      <span className="text-sm">{it}</span>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </motion.div>
                );
              })()}
            </AnimatePresence>
          </div>
        </div>
      </section>

      <section className="py-16 bg-teal-600 text-center text-white">
        <h2 className="font-heading text-3xl font-bold">{s.final_title}</h2>
        <p className="mt-3 text-teal-50">{s.final_subtitle}</p>
        <Link to="/contatti"><Button size="lg" className="mt-6 bg-white text-teal-700 hover:bg-teal-50 font-bold h-13 px-8 rounded-full" data-testid="services-contact-cta">{s.final_cta} <ArrowRight className="h-5 w-5 ml-1.5" /></Button></Link>
      </section>
    </PublicLayout>
  );
}
